/**
 * Auth API — login, logout, register, current-session probe. Task 3.3.
 */
import { ApiError } from "./client";

const BASE = "/api/auth";

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || "GET").toUpperCase();
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  return fetch(url, { ...init, credentials: "include", headers });
}

async function json<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await authedFetch(`${BASE}${path}`, {
    ...options,
    headers: { ...(options.headers || {}), "Content-Type": "application/json" },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const err = await res.json();
      msg = err.detail || msg;
    } catch {
      // ignore
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export interface AuthUser {
  id: string;
  email: string;
  role: "user" | "admin";
  email_verified?: boolean;
}

export interface Invite {
  id: string;
  code: string;
  email: string | null;
  used_at: string | null;
  created_at: string;
}

export const authApi = {
  login: (email: string, password: string) =>
    json<AuthUser>("/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string, invite_code: string) =>
    json<{ email: string; message: string }>("/register", {
      method: "POST",
      body: JSON.stringify({ email, password, invite_code }),
    }),
  logout: () => json<{ logged_out: boolean }>("/logout", { method: "POST" }),
  me: () => json<AuthUser>("/me"),
  verifyEmail: (token: string) =>
    json<AuthUser>("/verify-email", { method: "POST", body: JSON.stringify({ token }) }),
  resendVerification: (email: string) =>
    json<{ message: string }>("/resend-verification", { method: "POST", body: JSON.stringify({ email }) }),
  forgotPassword: (email: string) =>
    json<{ message: string }>("/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (token: string, new_password: string) =>
    json<{ message: string }>("/reset-password", { method: "POST", body: JSON.stringify({ token, new_password }) }),
  createInvite: (email?: string) =>
    json<{ code: string; email: string | null }>("/invites", { method: "POST", body: JSON.stringify({ email }) }),
  listInvites: () => json<Invite[]>("/invites"),
};
